import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { updateDefaultWeek } from "../../lib/api";
import { DEFAULT_LEGEND, resolveLegend, legendLabel } from "../../lib/calendarLegend";

function PrintPreviewMonth({ year, month, schoolCalendar, countSchoolDays }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const monthName = firstDay.toLocaleDateString('en-US', { month: 'long' });
  const schoolDays = countSchoolDays(year, month);
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getDateClass = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const specialDate = schoolCalendar.special_dates?.find(sd => sd.date === dateStr);
    const dayOfWeek = new Date(year, month, day).getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isFirstDay = dateStr === schoolCalendar.first_day;
    const isLastDay = dateStr === schoolCalendar.last_day;
    if (isFirstDay || isLastDay) return 'bg-green-400 font-bold';
    if (specialDate) {
      const typeInfo = DEFAULT_LEGEND.find(t => t.value === specialDate.type);
      return typeInfo?.color || 'bg-gray-200';
    }
    if (isWeekend) return 'text-gray-300';
    return '';
  };

  return (
    <div className="text-[8px] leading-tight">
      <div className="font-bold text-center mb-0.5">{monthName} ({schoolDays})</div>
      <div className="grid grid-cols-7 gap-px">
        {['S','M','T','W','T','F','S'].map((d,i) => <div key={i} className="text-center font-bold text-gray-500">{d}</div>)}
        {days.map((day, idx) => (
          <div key={idx} className={`text-center py-px ${day ? getDateClass(day) : ''}`}>{day || ''}</div>
        ))}
      </div>
    </div>
  );
}

export function SchoolYearCalendarTab({
  schoolCalendar, generateSchoolYearMonths, countSchoolDays,
  getSpecialDateInfo, handleDeleteSpecialDate, handleDeleteKeyDate,
  calendarDialogOpen, setCalendarDialogOpen,
  addDateDialogOpen, setAddDateDialogOpen,
  calendarFormData, setCalendarFormData,
  newSpecialDate, setNewSpecialDate,
  handleCreateCalendar, handleAddSpecialDate, handlePrintCalendar,
  onRefresh,
  allCalendars, selectedCalendarYear, onYearChange,
}) {
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const schoolYearCalendarRef = useRef(null);
  const [weekDialogOpen, setWeekDialogOpen] = useState(false);
  const [datePopup, setDatePopup] = useState({ open: false, date: null, info: null });
  const [defaultWeek, setDefaultWeek] = useState(() => {
    const saved = schoolCalendar?.default_week;
    return saved || { monday: "full", tuesday: "full", wednesday: "full", thursday: "full", friday: "full" };
  });
  const [monthlyPatterns, setMonthlyPatterns] = useState(() => schoolCalendar?.monthly_patterns || []);
  const [savingWeek, setSavingWeek] = useState(false);

  const DAY_NAMES = ["monday", "tuesday", "wednesday", "thursday", "friday"];
  const DAY_LABELS = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday" };
  const WEEKDAY_OPTIONS = [
    { value: 0, label: "Monday" }, { value: 1, label: "Tuesday" }, { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" }, { value: 4, label: "Friday" },
  ];

  const handleSaveDefaultWeek = async () => {
    setSavingWeek(true);
    try {
      const res = await updateDefaultWeek(schoolCalendar.academic_year, {
        default_week: defaultWeek,
        monthly_patterns: monthlyPatterns,
      });
      toast.success(res.data.message);
      setWeekDialogOpen(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSavingWeek(false); }
  };

  const legend = resolveLegend(schoolCalendar);
  const notice = (schoolCalendar?.notice || "").trim();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>School Year Calendar</CardTitle>
              <CardDescription>Create and print your school year calendar</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {/* Year Selector */}
              {allCalendars && allCalendars.length > 0 && (
                <Select value={selectedCalendarYear} onValueChange={onYearChange}>
                  <SelectTrigger className="w-[160px]" data-testid="calendar-year-select"><SelectValue placeholder="Select Year" /></SelectTrigger>
                  <SelectContent>
                    {allCalendars.map(c => (
                      <SelectItem key={c.academic_year} value={c.academic_year}>{c.academic_year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {schoolCalendar && (
                <>
                  <Dialog open={addDateDialogOpen} onOpenChange={setAddDateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="add-special-date-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M12 5v14M5 12h14"/></svg>
                        Add Date
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Add Special Date</DialogTitle></DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>Date Type</Label>
                          <Select value={newSpecialDate.type} onValueChange={(v) => setNewSpecialDate(p => ({ ...p, type: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {legend.map(t => (
                                <SelectItem key={t.value} value={t.value}>
                                  <span className="flex items-center gap-2"><span className={`w-3 h-3 rounded ${t.color}`}></span>{t.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input value={newSpecialDate.name} onChange={(e) => setNewSpecialDate(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Thanksgiving Break, MLK Day" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={newSpecialDate.date} onChange={(e) => setNewSpecialDate(p => ({ ...p, date: e.target.value }))} /></div>
                          <div className="space-y-2"><Label>End Date (for ranges)</Label><Input type="date" value={newSpecialDate.end_date} onChange={(e) => setNewSpecialDate(p => ({ ...p, end_date: e.target.value }))} /></div>
                        </div>
                        <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <Label className="text-sm font-medium">Recurring Weekly</Label>
                          <p className="text-xs text-muted-foreground">Apply to every occurrence of a specific day of the week for the school year</p>
                          <Select value={newSpecialDate.recurring_day || "none"} onValueChange={(v) => setNewSpecialDate(p => ({ ...p, recurring_day: v === "none" ? "" : v }))}>
                            <SelectTrigger className="w-full"><SelectValue placeholder="Not recurring (one-time)" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not recurring (one-time)</SelectItem>
                              <SelectItem value="1">Every Monday</SelectItem>
                              <SelectItem value="2">Every Tuesday</SelectItem>
                              <SelectItem value="3">Every Wednesday</SelectItem>
                              <SelectItem value="4">Every Thursday</SelectItem>
                              <SelectItem value="5">Every Friday</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setAddDateDialogOpen(false)}>Cancel</Button>
                          <Button onClick={handleAddSpecialDate} className="bg-[#00305E] hover:bg-[#00305E]/90">{newSpecialDate.recurring_day ? "Add Recurring Dates" : "Add Date"}</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  {/* Default Week Schedule */}
                  <Dialog open={weekDialogOpen} onOpenChange={(open) => {
                    setWeekDialogOpen(open);
                    if (open && schoolCalendar) {
                      setDefaultWeek(schoolCalendar.default_week || { monday: "full", tuesday: "full", wednesday: "full", thursday: "full", friday: "full" });
                      setMonthlyPatterns(schoolCalendar.monthly_patterns || []);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="default-week-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        Default Week
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader><DialogTitle>Default Week Schedule</DialogTitle></DialogHeader>
                      <p className="text-sm text-muted-foreground">Set the default schedule for each day of the week. This will automatically mark dates on the calendar.</p>
                      <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">Weekly Pattern</Label>
                          <div className="border rounded-lg divide-y">
                            {DAY_NAMES.map((day) => (
                              <div key={day} className="flex items-center justify-between p-3">
                                <span className="font-medium text-sm w-24">{DAY_LABELS[day]}</span>
                                <Select value={defaultWeek[day] || "full"} onValueChange={(v) => setDefaultWeek(prev => ({ ...prev, [day]: v }))}>
                                  <SelectTrigger className={`w-[180px] h-8 text-sm ${
                                    defaultWeek[day] === "early_release" ? "bg-yellow-50 border-yellow-300 text-yellow-800" :
                                    defaultWeek[day] === "no_school" ? "bg-pink-50 border-pink-300 text-pink-800" : ""
                                  }`} data-testid={`default-week-${day}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="full">Full Day</SelectItem>
                                    <SelectItem value="early_release">Early Release</SelectItem>
                                    <SelectItem value="no_school">No School</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">Monthly Patterns</Label>
                            <Button variant="outline" size="sm" onClick={() => setMonthlyPatterns(prev => [...prev, { day_of_week: 2, week_number: 1, type: "early_release", name: "" }])} data-testid="add-monthly-pattern">
                              Add Pattern
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">e.g., "First Wednesday of every month = Early Release"</p>
                          {monthlyPatterns.map((pattern, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                              <Select value={String(pattern.week_number)} onValueChange={(v) => {
                                const updated = [...monthlyPatterns]; updated[idx] = { ...updated[idx], week_number: parseInt(v) }; setMonthlyPatterns(updated);
                              }}>
                                <SelectTrigger className="w-[100px] h-8 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1st</SelectItem>
                                  <SelectItem value="2">2nd</SelectItem>
                                  <SelectItem value="3">3rd</SelectItem>
                                  <SelectItem value="4">4th</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={String(pattern.day_of_week)} onValueChange={(v) => {
                                const updated = [...monthlyPatterns]; updated[idx] = { ...updated[idx], day_of_week: parseInt(v) }; setMonthlyPatterns(updated);
                              }}>
                                <SelectTrigger className="w-[130px] h-8 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {WEEKDAY_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Select value={pattern.type} onValueChange={(v) => {
                                const updated = [...monthlyPatterns]; updated[idx] = { ...updated[idx], type: v }; setMonthlyPatterns(updated);
                              }}>
                                <SelectTrigger className="w-[140px] h-8 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="early_release">Early Release</SelectItem>
                                  <SelectItem value="no_school">No School</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input placeholder="Label (optional)" value={pattern.name || ""} className="h-8 text-sm flex-1"
                                onChange={(e) => { const updated = [...monthlyPatterns]; updated[idx] = { ...updated[idx], name: e.target.value }; setMonthlyPatterns(updated); }} />
                              <button onClick={() => setMonthlyPatterns(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600 p-1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                          Saving will auto-generate dates on the calendar for the entire school year. Manually added dates will not be overwritten.
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setWeekDialogOpen(false)}>Cancel</Button>
                          <Button onClick={handleSaveDefaultWeek} disabled={savingWeek} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-default-week-btn">
                            {savingWeek ? "Saving..." : "Save & Generate Dates"}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button onClick={handlePrintCalendar} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="print-calendar-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>
                    Print Calendar
                  </Button>
                  <Button variant="outline" onClick={() => setShowPrintPreview(p => !p)} data-testid="preview-calendar-btn">
                    {showPrintPreview ? "Hide Preview" : "Show Preview"}
                  </Button>
                </>
              )}
              <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant={schoolCalendar ? "outline" : "default"} className={!schoolCalendar ? "bg-[#00305E] hover:bg-[#00305E]/90" : ""}>
                    {schoolCalendar ? "Edit Settings" : "Create Calendar"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{schoolCalendar ? "Edit Calendar Settings" : "Create School Year Calendar"}</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2"><Label>School Name</Label><Input value={calendarFormData.school_name} onChange={(e) => setCalendarFormData(p => ({ ...p, school_name: e.target.value }))} /></div>
                    <div className="space-y-2">
                      <Label>Academic Year</Label>
                      <Select value={calendarFormData.academic_year} onValueChange={(v) => setCalendarFormData(p => ({ ...p, academic_year: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2024-2025">2024-2025</SelectItem>
                          <SelectItem value="2025-2026">2025-2026</SelectItem>
                          <SelectItem value="2026-2027">2026-2027</SelectItem>
                          <SelectItem value="2027-2028">2027-2028</SelectItem>
                          <SelectItem value="2028-2029">2028-2029</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2"><Label>First Day of School</Label><Input type="date" value={calendarFormData.first_day} onChange={(e) => setCalendarFormData(p => ({ ...p, first_day: e.target.value }))} /></div>
                      <div className="space-y-2"><Label>Last Day of School</Label><Input type="date" value={calendarFormData.last_day} onChange={(e) => setCalendarFormData(p => ({ ...p, last_day: e.target.value }))} /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>Message to Families <span className="text-xs text-muted-foreground">(shown on the calendar for staff, parents, district & transportation — leave blank to hide)</span></Label>
                      <Textarea
                        value={calendarFormData.notice || ""}
                        onChange={(e) => setCalendarFormData(p => ({ ...p, notice: e.target.value }))}
                        placeholder="e.g., Early dismissal on Friday due to weather. Buses run 2 hours early."
                        rows={3}
                        data-testid="calendar-notice-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Legend Labels <span className="text-xs text-muted-foreground">(rename how each calendar color is described)</span></Label>
                      <div className="space-y-2">
                        {DEFAULT_LEGEND.map((t) => (
                          <div key={t.value} className="flex items-center gap-2">
                            <span className={`w-4 h-4 rounded shrink-0 ${t.color}`}></span>
                            <Input
                              value={(calendarFormData.legend_labels || {})[t.value] ?? ""}
                              onChange={(e) => setCalendarFormData(p => ({ ...p, legend_labels: { ...(p.legend_labels || {}), [t.value]: e.target.value } }))}
                              placeholder={t.label}
                              data-testid={`legend-label-${t.value}`}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">Leave a field blank to use the default label ({DEFAULT_LEGEND.length} legend items).</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => setCalendarDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleCreateCalendar} className="bg-[#00305E] hover:bg-[#00305E]/90">{schoolCalendar ? "Save Changes" : "Create Calendar"}</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {schoolCalendar ? (
            <div className="space-y-6">
              {notice && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="calendar-notice">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 mt-0.5 shrink-0"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{notice}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-4 p-3 bg-gray-50 rounded-lg border text-sm">
                <span className="font-medium">Legend:</span>
                {legend.map(t => (<span key={t.value} className="flex items-center gap-1"><span className={`w-4 h-4 rounded ${t.color}`}></span>{t.label}</span>))}
              </div>
              <div ref={schoolYearCalendarRef}>
                <h1 className="text-2xl font-bold text-center mb-6">{schoolCalendar.school_name} - {schoolCalendar.academic_year}</h1>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {generateSchoolYearMonths().map(({ year, month }) => {
                    const firstDay = new Date(year, month, 1);
                    const lastDay = new Date(year, month + 1, 0);
                    const startDay = firstDay.getDay();
                    const daysInMonth = lastDay.getDate();
                    const monthName = firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    const days = [];
                    for (let i = 0; i < startDay; i++) days.push(null);
                    for (let d = 1; d <= daysInMonth; d++) days.push(d);
                    return (
                      <div key={`${year}-${month}`} className="border rounded-lg p-3">
                        <h3 className="font-bold text-center mb-2 text-[#00305E]">{monthName}</h3>
                        <div className="grid grid-cols-7 gap-1 text-xs">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (<div key={i} className="text-center font-bold text-gray-500 py-1">{d}</div>))}
                          {days.map((day, idx) => {
                            if (day === null) return <div key={idx} className="p-1"></div>;
                            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            const specialDate = getSpecialDateInfo(dateStr);
                            const dayOfWeek = new Date(year, month, day).getDay();
                            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                            const isFirstDay = dateStr === schoolCalendar.first_day;
                            const isLastDay = dateStr === schoolCalendar.last_day;
                            let bgColor = '', title = '';
                            if (isFirstDay) { bgColor = 'bg-green-400'; title = 'First Day of School'; }
                            else if (isLastDay) { bgColor = 'bg-green-400'; title = 'Last Day of School'; }
                            else if (specialDate) { bgColor = legend.find(t => t.value === specialDate.type)?.color || 'bg-gray-200'; title = specialDate.name; }
                            else if (isWeekend) { bgColor = 'bg-gray-100 text-gray-400'; }
                            return (
                              <div key={idx} className={`p-1 text-center rounded ${bgColor} cursor-pointer hover:opacity-80`} title={title}
                                onClick={() => {
                                  const dateStr2 = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                  const isFirst = dateStr2 === schoolCalendar.first_day;
                                  const isLast = dateStr2 === schoolCalendar.last_day;
                                  const info = specialDate || (isFirst ? { name: "First Day of School", type: "event", date: dateStr2 } : isLast ? { name: "Last Day of School", type: "event", date: dateStr2 } : null);
                                  setDatePopup({ open: true, date: dateStr2, info, isSpecialDate: !!specialDate });
                                }}>{day}</div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {schoolCalendar.special_dates?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3">Key Dates</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {/* First Day */}
                    {schoolCalendar.first_day && (
                      <div className="flex items-center justify-between p-2 rounded border bg-gray-50">
                        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-300"></span><span className="text-sm">First Day of School</span></div>
                        <span className="text-xs text-muted-foreground">{new Date(schoolCalendar.first_day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {/* Last Day */}
                    {schoolCalendar.last_day && (
                      <div className="flex items-center justify-between p-2 rounded border bg-gray-50">
                        <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-300"></span><span className="text-sm">Last Day of School</span></div>
                        <span className="text-xs text-muted-foreground">{new Date(schoolCalendar.last_day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                    {/* Only holidays and breaks (not early_release) */}
                    {[...new Map(schoolCalendar.special_dates.filter(d => d.type === 'holiday' || d.type === 'no_school' || d.type === 'vacation').map(d => [`${d.name}-${d.type}`, d])).values()]
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((date, idx) => {
                        const typeInfo = legend.find(t => t.value === date.type);
                        const sameDates = schoolCalendar.special_dates.filter(d => d.name === date.name && d.type === date.type);
                        const startDate = sameDates[0]?.date;
                        const endDate = sameDates.length > 1 ? sameDates[sameDates.length - 1]?.date : null;
                        return (
                          <div key={idx} className="flex items-center justify-between p-2 rounded border bg-gray-50 group" data-testid={`key-date-row-${date.name}-${date.type}`}>
                            <div className="flex items-center gap-2 min-w-0"><span className={`w-3 h-3 rounded shrink-0 ${typeInfo?.color || 'bg-gray-300'}`}></span><span className="text-sm truncate">{date.name}</span></div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {endDate ? `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : new Date(startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                              <button
                                type="button"
                                data-testid={`delete-key-date-${date.name}-${date.type}`}
                                title={`Delete "${date.name}"`}
                                onClick={() => { if (window.confirm(`Delete "${date.name}" from this calendar?`)) handleDeleteKeyDate(date.name, date.type); }}
                                className="text-gray-300 hover:text-red-600 transition-colors p-0.5 opacity-60 group-hover:opacity-100"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              {showPrintPreview && (
                <div className="mt-8 border-2 border-dashed border-gray-300 rounded-lg p-6 bg-white">
                  <div className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    Print Preview - This is how your calendar will look when printed
                  </div>
                  <div className="bg-white p-4 shadow-lg max-w-5xl mx-auto" style={{ aspectRatio: '11/8.5' }}>
                    <h2 className="text-lg font-bold text-center mb-2">{schoolCalendar.academic_year} {schoolCalendar.school_name} Calendar</h2>
                    {notice && (
                      <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded px-2 py-1 text-[9px] mb-2 whitespace-pre-wrap" data-testid="preview-notice">{notice}</div>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-[9px]">
                      {legend.map(t => (
                        <div key={t.value} className="flex items-center gap-1"><div className={`w-3 h-2 border border-gray-400 ${t.color}`}></div>{t.label}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {[7, 8, 9, 10].map(m => { const yr = parseInt(schoolCalendar.academic_year.split('-')[0]); return <PrintPreviewMonth key={m} year={yr} month={m} schoolCalendar={schoolCalendar} countSchoolDays={countSchoolDays} />; })}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {[11, 0, 1, 2].map(m => { const yr = parseInt(schoolCalendar.academic_year.split('-')[0]) + (m < 7 ? 1 : 0); return <PrintPreviewMonth key={m} year={yr} month={m} schoolCalendar={schoolCalendar} countSchoolDays={countSchoolDays} />; })}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[3, 4, 5].map(m => { const yr = parseInt(schoolCalendar.academic_year.split('-')[0]) + 1; return <PrintPreviewMonth key={m} year={yr} month={m} schoolCalendar={schoolCalendar} countSchoolDays={countSchoolDays} />; })}
                      <div></div>
                    </div>
                    <div className="mt-3 pt-2 border-t text-[9px]">
                      <div className="font-bold mb-1">Key Dates:</div>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-0.5">
                        {[...new Map((schoolCalendar.special_dates || []).filter(d => d.type === 'holiday' || d.type === 'no_school' || d.type === 'vacation').map(d => [`${d.name}-${d.type}`, d])).values()]
                          .sort((a, b) => a.date.localeCompare(b.date))
                          .map((event, idx) => {
                            const sameDates = schoolCalendar.special_dates.filter(d => d.name === event.name && d.type === event.type);
                            const sd = sameDates[0]?.date;
                            const ed = sameDates.length > 1 ? sameDates[sameDates.length - 1]?.date : null;
                            const fmt = (ds) => new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            return <div key={idx}>{event.name}: {ed ? `${fmt(sd)} - ${fmt(ed)}` : fmt(sd)}</div>;
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Date Click Popup */}
              <Dialog open={datePopup.open} onOpenChange={(open) => setDatePopup(p => ({ ...p, open }))}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>
                      {datePopup.date ? new Date(datePopup.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : ""}
                    </DialogTitle>
                  </DialogHeader>
                  {datePopup.info ? (
                    <div className="space-y-3 pt-2">
                      <div className={`p-3 rounded-lg border ${(legend.find(t => t.value === datePopup.info.type)?.color || 'bg-gray-100')} bg-opacity-30`}>
                        <p className="font-medium">{datePopup.info.name}</p>
                        <p className="text-xs mt-1 capitalize text-muted-foreground">{datePopup.info.type?.replace("_", " ")}</p>
                      </div>
                      {datePopup.isSpecialDate && (
                        <Button variant="outline" size="sm" className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => { handleDeleteSpecialDate(datePopup.info); setDatePopup({ open: false, date: null, info: null }); }}>
                          Remove This Date
                        </Button>
                      )}
                      <Button size="sm" className="w-full bg-[#00305E] hover:bg-[#00305E]/90" data-testid="popup-add-on-date"
                        onClick={() => {
                          setNewSpecialDate({ date: datePopup.date, end_date: "", type: "no_school", name: "", recurring_day: "" });
                          setDatePopup({ open: false, date: null, info: null });
                          setAddDateDialogOpen(true);
                        }}>
                        Add Another Event on This Date
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-2 text-center">
                      <p className="text-muted-foreground py-2">No events on this day.</p>
                      <Button size="sm" className="w-full bg-[#00305E] hover:bg-[#00305E]/90" data-testid="popup-add-on-date"
                        onClick={() => {
                          setNewSpecialDate({ date: datePopup.date, end_date: "", type: "no_school", name: "", recurring_day: "" });
                          setDatePopup({ open: false, date: null, info: null });
                          setAddDateDialogOpen(true);
                        }}>
                        Add Event on This Date
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <p className="text-muted-foreground mb-4">No school year calendar created yet</p>
              <Button onClick={() => setCalendarDialogOpen(true)} className="bg-[#00305E] hover:bg-[#00305E]/90">Create School Year Calendar</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
